import { useFormat, useI18n } from '@/i18n'
import { Banner, BilingualLabel, Card, CardBody, CardHeader } from '@/ui'
import type { PackRunView, ProcessState } from '@/features/process/projection'
import { Line } from '@/features/process/components/bits'
import { formatBp, pctToBp } from '@/features/process/model/balance'
import { cartonSplit, lotState, reconcile } from '../../model/reconcile'
import { LotStateBadge } from '../../components/LotStateBadge'

/** After close: counts, reconciliation, carton split, weight balance once synced, lot state. */
export function ClosedPackSection({ state, run }: { state: ProcessState; run: PackRunView }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const lot = run.pack_lot ? (state.packLots.get(run.pack_lot.id) ?? run.pack_lot) : null
  const c = run.close
  const rec = c
    ? reconcile({ printed: c.labels_printed, applied: c.pouches_good, spoiled: c.labels_spoiled, destroyed: c.labels_destroyed })
    : null
  const pouches = lot?.pouch_count ?? c?.pouches_good ?? null
  const split = pouches ? cartonSplit(pouches, run.sku.pouches_per_carton) : null
  const s = lot ? lotState(lot) : null

  return (
    <div className="flex flex-col gap-4">
      {s === 'WAITING' && (
        <Banner tone="info" title={<BilingualLabel k="pack.closed.waitingTitle" />}>
          {t('pack.closed.waitingBody')}
        </Banner>
      )}
      {s === 'HELD' && <Banner tone="warn">{t('pack.lots.heldHint')}</Banner>}
      {s === 'RELEASED' && <Banner tone="success">{t('pack.closed.released')}</Banner>}
      {lot && (
        <Card>
          <CardHeader title={<span className="font-mono">{lot.code}</span>} actions={<LotStateBadge lot={lot} />} />
          <CardBody className="py-1">
            {pouches !== null && <Line label={t('pack.close.applied')} value={pouches} strong />}
            {split && (
              <Line
                label={t('pack.closed.cartons')}
                value={t('pack.cartons', { full: split.full, per: run.sku.pouches_per_carton, partial: split.partial })}
              />
            )}
            {rec && (
              <Line
                label={t('pack.close.reconciliation')}
                value={rec.ok ? t('pack.close.addsUp') : t('pack.close.gap', { n: rec.gap })}
                tone={rec.ok ? 'ok' : 'warn'}
              />
            )}
          </CardBody>
        </Card>
      )}
      {run.balance ? (
        <Card>
          <CardHeader eyebrow={<BilingualLabel k="pack.closed.balance" />} />
          <CardBody className="py-1">
            <Line label={t('pack.close.weightIn')} value={fmt.weight(run.balance.input_g)} />
            <Line label={t('pack.closed.packed')} value={fmt.weight(run.balance.packed_g)} />
            <Line label={t('pack.close.leftover')} value={fmt.weight(run.balance.leftover_g)} />
            <Line label={t('pack.close.spillage')} value={fmt.weight(run.balance.spillage_g)} />
            <Line
              label={t('pack.closed.unaccounted')}
              value={formatBp(pctToBp(run.balance.unaccounted_pct) ?? 0)}
              tone={run.balance.in_limit ? 'ok' : 'warn'}
              strong
            />
          </CardBody>
        </Card>
      ) : (
        c && <p className="text-[13px] text-muted">{t('pack.close.balanceAfterSync')}</p>
      )}
    </div>
  )
}
